import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  quantitiesByVariant,
  startViewTransition,
  startViewTransitionWithPhases,
} from '@theme/utilities';
import {
  buildSectionSelector,
  canCommitCartWrite,
  cartCommitTokenOf,
  cartRenderWorkSettled,
  cartCommitTokenFor,
  claimCartCommitToken,
  morphSection,
  nextCartCommitToken,
  registerCartRenderWork,
  sectionRenderer,
  stampSurfaceRevision,
} from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartSectionRestoredEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

const CART_MUTATION_EVENTS = [
  StandardEvents.cartLinesUpdate,
  StandardEvents.cartDiscountUpdate,
  StandardEvents.cartNoteUpdate,
];

function createRenderWork() {
  let settle = () => {};
  let fail = () => {};
  const promise = new Promise((resolve, reject) => {
    settle = resolve;
    fail = reject;
  });
  promise.catch(() => {});
  return { promise, settle, fail };
}

export class CartItemsComponent extends createViewEventElement(Component) {

  static #pendingQuantityUpdates = new Set();

  static get pendingQuantityUpdates() {
    return Array.from(CartItemsComponent.#pendingQuantityUpdates);
  }

  static #CHECKOUT_WAIT_MS = 10000;

  #queuedIntents = new Map();
  #debouncedSend = debounce(() => this.#sendQueuedIntents(), 300);

  #saveTail = null;

  #unsettledIntents = new Map();
  #saveBlocked = false;
  #saveGeneration = 0;

  #checkoutAttempt = null;
  #replayingCheckout = false;
  #nativeCheckoutEvent = null;
  #externalCartWrites = new Set();

  #internalCartWrites = new Set();
  #lineWriteCount = 0;

  #pendingCartFetch = null;
  #connectionEpoch = 0;

  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  async #refreshCartSection(options = {}) {
    const connectionEpoch = this.#connectionEpoch;
    try {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        ...options,
      });
      if (this.isConnected && connectionEpoch === this.#connectionEpoch) {
        this.#updateCartQuantitySelectorButtonStates();
      }
      return null;
    } catch (error) {
      if (error?.name === 'AbortError') return null;

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to refresh cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      return error;
    }
  }

  #handleDiscountUpdate = (event) => {

    const isExternalCartUpdate = this.#isExternalCartUpdate(event);
    const promise = event.promise;
    if (!promise) return;

    const cartCommitToken = cartCommitTokenOf(promise) ?? nextCartCommitToken();
    const { promise: renderWork, settle: settleRenderWork, fail: failRenderWork } = createRenderWork();
    registerCartRenderWork(promise, renderWork);
    let renderError = null;

    promise
      .then(async ({ detail }) => {
        try {
          if (!isExternalCartUpdate) return;

          const sectionsHtml = detail?.sections?.[this.sectionId];
          if (sectionsHtml) {
            sectionRenderer.abortRender(this.sectionId);
            await morphSection(this.sectionId, sectionsHtml, {
              mode: this.isDrawer ? 'hydration' : 'full',
              cartCommitToken,
            });
            this.#updateCartQuantitySelectorButtonStates();
          } else {
            renderError = await this.#refreshCartSection({ cartCommitToken });
          }
        } catch (error) {

          if (error?.name !== 'AbortError') renderError = error;
          throw error;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      })
      .finally(() => {
        if (renderError) failRenderWork(renderError);
        else settleRenderWork();
      });
  };

  #handleNoteUpdate = (event) => {
    if (!this.#isExternalCartUpdate(event)) return;

    const promise = event.promise;
    if (!promise) return;

    const cartCommitToken = cartCommitTokenOf(promise) ?? nextCartCommitToken();
    const connectionEpoch = this.#connectionEpoch;
    const { promise: renderWork, settle: settleRenderWork, fail: failRenderWork } = createRenderWork();
    registerCartRenderWork(promise, renderWork);
    let renderError = null;

    promise
      .then(async ({ detail }) => {
        try {
          if (!this.isConnected || connectionEpoch !== this.#connectionEpoch) return;
          const sections = detail?.sections;
          const sectionsHtml = sections?.[this.sectionId];
          if (sectionsHtml) {
            sectionRenderer.abortRender(this.sectionId);
            const committed = await morphSection(this.sectionId, sectionsHtml, {
              mode: this.isDrawer ? 'hydration' : 'full',
              cartCommitToken,
            });
            if (committed !== false && this.isConnected && connectionEpoch === this.#connectionEpoch) {
              this.#updateCartQuantitySelectorButtonStates();
            }
          } else {
            renderError = await this.#refreshCartSection({ cartCommitToken });
          }
        } catch (error) {

          if (error?.name !== 'AbortError') renderError = error;
          throw error;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      })
      .finally(() => {
        if (renderError) failRenderWork(renderError);
        else settleRenderWork();
      });
  };

  connectedCallback() {
    super.connectedCallback();
    this.#connectionEpoch += 1;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
    window.addEventListener('pagehide', this.#handlePageHide);
    for (const type of CART_MUTATION_EVENTS) document.addEventListener(type, this.#observeCartWrite, true);
    this.addEventListener('formdata', this.#checkoutFormData, true);
    this.addEventListener('keydown', this.#handleRemoveControl);
    this.addEventListener('auxclick', this.#handleRemoveControl);
    this.#enhanceRemoveControls();
  }

  #handleRemoveControl = (event) => {
    const control = event.target instanceof Element ? event.target.closest('.cart-items__remove') : null;
    if (!(control instanceof HTMLElement)) return;
    if (event.type === 'auxclick') {
      event.preventDefault();
      return;
    }
    if (event.key !== ' ') return;
    event.preventDefault();
    if (!event.repeat) control.click();
  };

  #enhanceRemoveControls() {
    for (const control of this.querySelectorAll('a.cart-items__remove')) control.setAttribute('role', 'button');
  }

  updatedCallback() {
    super.updatedCallback();
    this.#restoreQuantityIntents();
    this.#enhanceRemoveControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#connectionEpoch += 1;

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);

    this.#debouncedSend.cancel();
    this.#queuedIntents.clear();
    this.#cancelCheckoutAttempt();
    this.#nativeCheckoutEvent = null;
    this.removeEventListener('formdata', this.#checkoutFormData, true);
    this.removeEventListener('keydown', this.#handleRemoveControl);
    this.removeEventListener('auxclick', this.#handleRemoveControl);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('pagehide', this.#handlePageHide);
    for (const type of CART_MUTATION_EVENTS) document.removeEventListener(type, this.#observeCartWrite, true);
  }

  #handlePageHide = () => this.#cancelCheckoutAttempt();

  #observeCartWrite = (event) => {
    if (event.target === this || !event.promise) return;
    const operation = event.promise;
    const settled = Promise.resolve(operation)
      .catch(() => {})
      .then(() => cartRenderWorkSettled(operation));

    if (event.type !== StandardEvents.cartLinesUpdate && !this.#isExternalCartUpdate(event)) {
      this.#internalCartWrites.add(settled);
      void settled.finally(() => this.#internalCartWrites.delete(settled));
      return;
    }
    if (event.type === StandardEvents.cartLinesUpdate) this.#lineWriteCount += 1;
    this.#externalCartWrites.add(operation);
    if (this.#checkoutAttempt && this.#isLiveAttempt(this.#checkoutAttempt)) {
      this.#showCheckoutStatus('retry');
      this.#cancelCheckoutAttempt();
    }
    void settled.finally(() => this.#externalCartWrites.delete(operation));
  };

  #handlePageShow = (event) => {

    const navigationEntry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    const navigationType = CartItemsComponent.#isNavigationTiming(navigationEntry) ? navigationEntry.type : undefined;
    if (!event.persisted && navigationType !== 'back_forward') return;
    this.#cancelCheckoutAttempt();

    if (!this.dataset.sectionId) return;

    sectionRenderer
      .renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        cartCommitToken: nextCartCommitToken(),

        injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
      })
      .then(() => {

        this.#updateCartQuantitySelectorButtonStates();

        this.dispatchEvent(new CartSectionRestoredEvent());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] bfcache restore render failed:', error);
      });
  };

  #onQuantitySelectorUpdate = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const key = this.#rowForEvent(event)?.dataset.key;
    const { quantity } = event.detail;
    if (!key || !Number.isFinite(quantity)) return;

    this.#queuedIntents.set(key, quantity);
    this.#unsettledIntents.set(key, { quantity });
    if (this.#checkoutAttempt?.recovering) {
      this.#checkoutAttempt.changed = true;
      this.#checkoutAttempt.intents.set(key, quantity);
      return;
    }
    this.#debouncedSend();
  };

  #liveRows() {
    return [...this.querySelectorAll('[ref="cartItemRows[]"]')].filter((row) => row instanceof HTMLElement);
  }

  #rowForEvent(event) {
    const row = event.target instanceof Element ? event.target.closest('[ref="cartItemRows[]"]') : null;
    if (row instanceof HTMLElement && this.contains(row)) return row;
    return event.detail.cartLine ? this.#liveRows()[event.detail.cartLine - 1] : undefined;
  }

  #rowByKey(key) {
    return this.#liveRows().find((row) => row.dataset.key === key);
  }

  #sendQueuedIntents() {
    const intents = [...this.#queuedIntents];
    this.#queuedIntents.clear();

    for (const [key, quantity] of intents) {
      const row = this.#rowByKey(key);
      if (quantity === 0) {
        this.updateQuantity({ key, quantity, action: 'clear' });
        if (row) this.#animateRemoval(row);
        continue;
      }

      this.updateQuantity({ key, quantity, action: 'change' });
      row?.querySelector('text-component')?.shimmer();
    }
  }

  onLineItemRemove(line, event) {
    event?.preventDefault();

    const row = event ? this.#rowForEvent(event) : this.#liveRows()[line - 1];
    const key = row?.dataset.key;
    if (!key) return;

    this.#queuedIntents.delete(key);
    this.updateQuantity({ key, quantity: 0, action: 'clear' });
    this.#animateRemoval(row);
  }

  #animateRemoval(cartItemRowToRemove) {
    const { cartItemRows } =   (this.refs);

    const rowsToRemove = [
      cartItemRowToRemove,

      ...cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    const isEmptyCart = rowsToRemove.length == cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      const emptyCartToken = nextCartCommitToken();
      const sectionRoot = document.getElementById(buildSectionSelector(this.sectionId));

      startViewTransition(() => {

        if (!canCommitCartWrite(sectionRoot, emptyCartToken)) return;

        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
        stampSurfaceRevision(sectionRoot, emptyCartToken);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      onAnimationEnd(row, remove);
    });
  }

  updateQuantity(config) {
    const key = config.key ?? this.#liveRows()[config.line - 1]?.dataset.key;
    const intent = { quantity: config.quantity };
    const generation = this.#saveGeneration;
    const lineWrites = this.#lineWriteCount;
    const queued = this.#saveTail !== null;
    if (key) {
      this.#unsettledIntents.set(key, intent);
      this.#checkoutAttempt?.intents.set(key, config.quantity);
    }

    let release = () => {};

    const pending = new Promise((resolve) => {
      release = resolve;
    });
    CartItemsComponent.#pendingQuantityUpdates.add(pending);

    const run = async () => {
      if (generation !== this.#saveGeneration || !this.isConnected) return 'unknown';
      if (queued || this.#saveBlocked) {

        const cart = await this.#readCart();
        if (
          !this.isConnected ||
          lineWrites !== this.#lineWriteCount ||
          this.#externalCartWrites.size ||
          !cart?.items.some((item) => item.key === key)
        ) {
          return 'unknown';
        }
        this.#saveBlocked = false;
      }
      if (config.checkoutAttempt && !this.#isLiveAttempt(config.checkoutAttempt)) return 'unknown';
      const input = this.#rowByKey(key)?.querySelector('input[name="updates[]"]');
      if (input instanceof HTMLInputElement && input.matches(':disabled')) return 'unknown';
      return this.#saveQuantity({ key, quantity: config.quantity, action: config.action }).catch(() => 'unknown');
    };
    const save = this.#saveTail ? this.#saveTail.then(run) : run();
    const done = save.then((result) => {

      const outcome = result === 'adjusted' ? 'unknown' : result;
      if (result === 'adjusted') intent.adjusted = true;
      if (outcome !== 'saved') {
        this.#saveBlocked = true;
        this.#saveGeneration += 1;
      }
      try {
        if (key && outcome !== 'unknown' && this.#unsettledIntents.get(key) === intent) {
          this.#unsettledIntents.delete(key);
        }
        this.#restoreQuantityIntents();
        this.#checkoutAttempt?.outcomes.push(outcome);
        if (outcome === 'unknown' && !this.#checkoutAttempt && this.isConnected) this.#showCheckoutStatus('review');
      } catch (error) {

        console.error('[cart-items] Quantity save cleanup failed:', error);
      }
      CartItemsComponent.#pendingQuantityUpdates.delete(pending);
      release(outcome === 'rejected');
      return outcome;
    });

    this.#saveTail = done;
    done.then(() => {
      if (this.#saveTail === done) this.#saveTail = null;
    });
    return done;
  }

  #restoreQuantityIntents() {
    for (const [key, { quantity }] of this.#unsettledIntents) {
      const input = this.#rowByKey(key)?.querySelector('input[name="updates[]"]');
      if (input instanceof HTMLInputElement && !input.matches(':disabled')) input.value = String(quantity);
    }
  }

  async #saveQuantity({ key, quantity, action }) {
    if (!key) {
      console.warn('[cart-items] Skipped a quantity save with no line key');
      return 'unknown';
    }

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

    const cartCommitToken = claimCartCommitToken(deferredUpdatePromise.promise);
    const { promise: renderWork, settle: settleRenderWork, fail: failRenderWork } = createRenderWork();
    registerCartRenderWork(deferredUpdatePromise.promise, renderWork);

    let renderPhase = false;
    let renderError = null;
    let outcome = 'unknown';

    try {
      this.#disableCartItems();

      const { cartTotal } =   (this.refs);

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      for (const item of cartItemsComponents) {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      }

      for (const sectionId of sectionsToUpdate) {
        sectionRenderer.abortRender(sectionId);
      }

      const body = JSON.stringify({
        id: key,
        quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      cartTotal?.shimmer();

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: action === 'change' && quantity > 0 ? 'update' : 'remove',
          context: 'cart',
          lines: [{ id: key, quantity }],
          promise: deferredUpdatePromise.promise,
        })
      );

      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const responseText = await response.text();
      const parsedResponseText = JSON.parse(responseText);

      resetShimmer(this);

      if (parsedResponseText.errors) {
        this.#handleCartError(key, parsedResponseText);
        outcome = 'rejected';
        deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
        return outcome;
      }

      const sections =   (parsedResponseText.sections);
      const sectionHTML = sections?.[this.sectionId];
      const confirmed = response.ok && this.#quantitiesMatch(parsedResponseText, new Map([[key, quantity]]));
      const parsedItemCount = Number(parsedResponseText.item_count);
      let newCartItemCount = Number.isFinite(parsedItemCount) ? parsedItemCount : 0;

      this.#updateQuantitySelectors(parsedResponseText);

      if (!sectionHTML) {
        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: sections ?? undefined,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        renderPhase = true;

        const failedEdit = !response.ok && quantity > 0;
        renderError = await this.#refreshCartSection({
          cartCommitToken,
          shouldRender: () => !failedEdit || !this.#checkoutAttempt,
        });
        if (!renderError) outcome = confirmed ? 'saved' : response.ok ? 'adjusted' : 'unknown';
        return outcome;
      }

      const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');

      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : newCartItemCount;

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
        detail: {
          sections: sections ?? undefined,
          items: parsedResponseText.items,
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          didError: false,
        },
      });

      renderPhase = true;
      sectionRenderer.abortRender(this.sectionId);
      await morphSection(this.sectionId, sectionHTML, {
        mode: this.isDrawer ? 'hydration' : 'full',
        cartCommitToken,
      });

      outcome = confirmed ? 'saved' : response.ok ? 'adjusted' : 'unknown';
      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      outcome = 'unknown';
      if (renderPhase && error?.name !== 'AbortError') renderError = error;
      console.error(error);
      deferredUpdatePromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to update cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      if (renderError) failRenderWork(renderError);
      else settleRenderWork();
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
    return outcome;
  }

  holdCheckoutForQuantitySave = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form !== this.querySelector('#cart-form')) return;

    const { submitter } = event;
    const isCheckout =
      (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) &&
      submitter.form === form &&
      submitter.name === 'checkout';

    if (!isCheckout) {
      if (!event.defaultPrevented) this.#cancelCheckoutAttempt();
      return;
    }
    this.#nativeCheckoutEvent = null;
    if (event.defaultPrevented) return;
    if (this.#checkoutAttempt) {
      event.preventDefault();
      return;
    }

    const retained = new Map([...this.#unsettledIntents].map(([key, intent]) => [key, intent.quantity]));
    let unrepresented = false;
    for (const input of this.#dirtyQuantityInputs(form)) {
      const key = input.closest('[data-key]')?.dataset.key;

      const quantity = this.#typedQuantity(input);
      if (!key || quantity === null) {
        event.preventDefault();
        this.#showCheckoutStatus('review');
        return;
      }
      retained.set(key, quantity);
      if (this.#unsettledIntents.get(key)?.quantity !== quantity) {
        unrepresented = true;
        this.#unsettledIntents.set(key, { quantity });
        this.#queuedIntents.set(key, quantity);
      }
    }
    if (this.#externalCartWrites.size) {
      event.preventDefault();

      if (unrepresented) this.#debouncedSend();
      this.#showCheckoutStatus('retry');
      return;
    }
    if (this.#replayingCheckout) {
      this.#nativeCheckoutEvent = event;
      return;
    }
    const active = document.activeElement;
    const blurred = active instanceof HTMLInputElement && active.form === form ? active : null;
    blurred?.blur();
    if (!submitter.formNoValidate && !form.noValidate && !form.reportValidity()) {
      event.preventDefault();
      return;
    }
    this.#debouncedSend.flush();

    const dirty = this.#dirtyQuantityInputs(form);
    if (
      !dirty.length &&
      !retained.size &&
      !this.#saveTail &&
      !CartItemsComponent.#pendingQuantityUpdates.size &&
      !this.#internalCartWrites.size
    ) {
      this.#nativeCheckoutEvent = event;
      return;
    }

    event.preventDefault();

    const attempt = {
      form,
      submitter,
      blurred,
      epoch: this.#connectionEpoch,
      cancelled: false,
      cartOnly: dirty.length > 1 || unrepresented || this.#saveBlocked,
      recovering: false,
      changed: false,
      submitterSignature: this.#submissionSignature(submitter),
      formSignature: this.#formSignature(form),

      intents: new Map([...this.#unsettledIntents].map(([key, intent]) => [key, intent.quantity])),
      outcomes: [],
    };
    for (const input of dirty) {
      const key = input.closest('[data-key]')?.dataset.key;
      const quantity = this.#typedQuantity(input);
      if (key && quantity !== null) attempt.intents.set(key, quantity);
    }
    this.#checkoutAttempt = attempt;

    let timer;
    const expiry = new Promise((resolve) => {
      timer = setTimeout(() => {
        if (this.#isLiveAttempt(attempt)) {
          this.#showCheckoutStatus('retry');
          attempt.cancelled = true;
        }
        resolve();
      }, CartItemsComponent.#CHECKOUT_WAIT_MS);
    });
    try {
      await Promise.race([this.#continueCheckout(attempt), expiry]);
    } finally {
      clearTimeout(timer);
      if (this.#checkoutAttempt === attempt) this.#checkoutAttempt = null;

      if (this.#queuedIntents.size && this.isConnected && !this.#checkoutAttempt) this.#debouncedSend();
    }
  };

  async #continueCheckout(attempt) {
    await this.#waitForQuantitySaves(attempt);
    if (!this.#isLiveAttempt(attempt)) return;
    if (attempt.outcomes.includes('rejected')) return this.#restoreCheckoutFocus(attempt);
    if (attempt.outcomes.includes('unknown')) return this.#showCheckoutStatus('review');

    const form = this.#liveCartForm(attempt);
    if (!form) return;
    if (!attempt.submitter.formNoValidate && !form.noValidate && !form.reportValidity()) return;

    if (attempt.cartOnly) {
      attempt.recovering = true;
      const recovered = await this.#revalidateIntents(attempt);
      if (!this.#isLiveAttempt(attempt)) return;
      if (recovered === 'rejected') return this.#restoreCheckoutFocus(attempt);
      return this.#showCheckoutStatus(recovered === 'saved' ? 'updated' : 'review');
    }

    const revision = cartCommitTokenFor();
    const cart = await this.#readCart();
    if (!this.#isLiveAttempt(attempt)) return;
    if (revision !== cartCommitTokenFor() || !this.#quantitiesMatch(cart, attempt.intents)) {
      for (const [key, quantity] of attempt.intents) {
        if (this.#unsettledIntents.get(key)?.quantity !== quantity) this.#unsettledIntents.set(key, { quantity });
      }
      this.#restoreQuantityIntents();
      this.#saveBlocked = true;
      return this.#showCheckoutStatus('review');
    }
    const liveForm = this.#liveCartForm(attempt);
    if (!liveForm || this.#dirtyQuantityInputs(liveForm).length) return this.#showCheckoutStatus('retry');
    const button = this.#liveCheckoutSubmitter(attempt, liveForm);
    if (!button) return this.#showCheckoutStatus('retry');
    this.#checkoutAttempt = null;
    this.#replayingCheckout = true;
    try {
      liveForm.requestSubmit(button);
    } finally {
      this.#replayingCheckout = false;
    }
  }

  #checkoutFormData = (event) => {
    const submission = this.#nativeCheckoutEvent;
    if (!submission || submission.eventPhase !== Event.NONE) return;
    this.#nativeCheckoutEvent = null;
    if (submission.defaultPrevented || !submission.isTrusted || event.target !== submission.target) return;
    event.formData.delete('updates[]');
  };

  #dirtyQuantityInputs(form) {
    return [...form.querySelectorAll('input[name="updates[]"]')].filter(
      (input) => input instanceof HTMLInputElement && !input.disabled && input.value !== input.defaultValue
    );
  }

  #typedQuantity(input) {
    const value = input.value.trim();
    const quantity = Number(value);
    return value !== '' && Number.isInteger(quantity) && quantity >= 0 ? quantity : null;
  }

  async #waitForQuantitySaves(attempt) {
    while (this.#isLiveAttempt(attempt)) {
      this.#debouncedSend.flush();
      const pending = [...CartItemsComponent.pendingQuantityUpdates, ...this.#internalCartWrites];
      if (this.#saveTail) pending.push(this.#saveTail);
      if (!pending.length) return;
      await Promise.all(pending);
    }
  }

  async #revalidateIntents(attempt) {
    this.#debouncedSend.cancel();
    this.#queuedIntents.clear();
    const revision = cartCommitTokenFor();
    let cart = await this.#readCart();
    if (!cart || revision !== cartCommitTokenFor()) return 'review';

    for (const [key, quantity] of attempt.intents) {
      if (!this.#isLiveAttempt(attempt) || attempt.changed) return 'review';
      const line = cart.items.find((item) => item.key === key);
      if (!line) {
        if (quantity === 0) continue;
        await this.#discardIntent(key);
        return 'review';
      }
      if (line.quantity === quantity) continue;
      const input = this.#rowByKey(key)?.querySelector('input[name="updates[]"]');
      if (!(input instanceof HTMLInputElement) || input.matches(':disabled')) return 'review';
      const intent = this.#unsettledIntents.get(key);
      if (intent?.adjusted && intent.quantity === quantity) {
        await this.#discardIntent(key);
        return 'review';
      }

      this.#saveBlocked = false;
      const outcome = await this.updateQuantity({
        key, quantity, action: quantity > 0 ? 'change' : 'clear', checkoutAttempt: attempt,
      });
      if (outcome !== 'saved') return outcome === 'rejected' ? 'rejected' : 'review';
      if (!this.#isLiveAttempt(attempt) || attempt.changed) return 'review';
      const revision = cartCommitTokenFor();
      cart = await this.#readCart();
      if (!cart || revision !== cartCommitTokenFor()) return 'review';
    }

    if (!this.#isLiveAttempt(attempt) || attempt.changed || !this.#quantitiesMatch(cart, attempt.intents)) return 'review';
    for (const [key, quantity] of attempt.intents) {
      if (this.#unsettledIntents.get(key)?.quantity === quantity) this.#unsettledIntents.delete(key);
    }
    this.#saveBlocked = false;
    return 'saved';
  }

  #quantitiesMatch(cart, intents) {
    if (!Array.isArray(cart?.items)) return false;
    return [...intents].every(([key, quantity]) => {
      const lines = cart.items.filter((item) => item.key === key);
      return quantity === 0 ? lines.length === 0 : lines.length === 1 && lines[0].quantity === quantity;
    });
  }

  async #discardIntent(key) {
    const renderError = await this.#refreshCartSection({ cartCommitToken: nextCartCommitToken() });
    if (renderError) return;
    this.#unsettledIntents.delete(key);
    const input = this.#rowByKey(key)?.querySelector('input[name="updates[]"]');
    if (input instanceof HTMLInputElement) input.value = input.defaultValue;
  }

  async #readCart() {
    try {
      const cart = await this.fetchCartData({ fresh: true });
      return Array.isArray(cart?.items) ? cart : null;
    } catch {
      return null;
    }
  }

  #isLiveAttempt(attempt) {
    return this.#checkoutAttempt === attempt && !attempt.cancelled && this.isConnected &&
      attempt.epoch === this.#connectionEpoch && this.#liveCartForm(attempt) !== null;
  }

  #liveCartForm(attempt) {
    if (attempt.form.isConnected && this.contains(attempt.form)) return attempt.form;
    const form = attempt.form.id ? document.getElementById(attempt.form.id) : null;
    return form instanceof HTMLFormElement && this.contains(form) ? form : null;
  }

  #liveCheckoutSubmitter(attempt, liveForm) {
    const { submitter } = attempt;
    const candidate = submitter.isConnected
      ? submitter
      : submitter.id
        ? document.getElementById(submitter.id)
        : null;
    if (!(candidate instanceof HTMLButtonElement || candidate instanceof HTMLInputElement)) return null;
    if (candidate.form !== liveForm || candidate.name !== 'checkout' || candidate.matches(':disabled')) return null;
    if (candidate.closest('[inert], [aria-busy="true"]')) return null;
    if (this.#submissionSignature(candidate) !== attempt.submitterSignature ||
        this.#formSignature(liveForm) !== attempt.formSignature) return null;
    return candidate;
  }

  #submissionSignature(submitter) {
    return JSON.stringify(['type', 'name', 'value', 'formaction', 'formmethod', 'formenctype', 'formtarget', 'formnovalidate']
      .map((name) => submitter.getAttribute(name)));
  }

  #formSignature(form) {
    return JSON.stringify(['action', 'method', 'enctype', 'target', 'novalidate'].map((name) => form.getAttribute(name)));
  }

  #restoreCheckoutFocus({ blurred }) {
    const focusMoved = document.activeElement && document.activeElement !== document.body;
    if (!focusMoved && blurred?.isConnected && blurred.form && this.contains(blurred.form)) blurred.focus();
  }

  #showCheckoutStatus(kind) {
    const status = this.querySelector('[data-cart-checkout-status]');
    if (!(status instanceof HTMLElement)) return;
    const messages = {
      retry: status.dataset.messageRetry,
      updated: status.dataset.messageUpdated,
      review: status.dataset.messageReview,
    };
    status.textContent = messages[kind] ?? '';
  }

  #cancelCheckoutAttempt() {
    if (this.#checkoutAttempt) this.#checkoutAttempt.cancelled = true;
    this.#checkoutAttempt = null;
    this.#nativeCheckoutEvent = null;
  }

  #handleCartError = (key, parsedResponseText) => {
    const row = this.#rowByKey(key);
    if (!row) throw new Error('Cart line not found');
    const quantityInput = row.querySelector('input[name="updates[]"]');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = row.querySelector('[ref^="cartItemError-"]');
    const cartItemErrorContainer = row.querySelector('[ref^="cartItemErrorContainer-"]');

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    const cartCommitToken = cartCommitTokenOf(event.promise) ?? nextCartCommitToken();

    const { promise: renderWork, settle: settleRenderWork, fail: failRenderWork } = createRenderWork();
    registerCartRenderWork(event.promise, renderWork);
    let renderError = null;

    event.promise
      ?.then(async ({ detail }) => {
        try {
          const sections = detail?.sections;
          const cartItemsHtml = sections?.[this.sectionId];

          const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;

          const mode = this.isDrawer ? 'hydration' : 'full';
          const morphOptions = {
            mode,
            injectStylesheet: wasEmptyCartDrawer,
            cartCommitToken,
          };

          if (cartItemsHtml) {
            sectionRenderer.abortRender(this.sectionId);
            const { cartItemRows: existingRows } =   (this.refs);
            const existingKeys = new Set(existingRows?.map((row) => row.dataset.key) ?? []);

            if (wasEmptyCartDrawer) {

              const { updateDone } = startViewTransitionWithPhases(
                () => morphSection(this.sectionId, cartItemsHtml, morphOptions),
                ['fill-cart-drawer']
              );
              await updateDone;
            } else {
              await morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }

            if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
              const { cartItemRows: updatedRows } =   (this.refs);
              for (const row of updatedRows ?? []) {
                if (!existingKeys.has(row.dataset.key)) {
                  row.classList.add('adding');
                  onAnimationEnd(row, () => row.classList.remove('adding'));
                }
              }
            }

            this.#updateCartQuantitySelectorButtonStates();
          } else {
            renderError = await this.#refreshCartSection(morphOptions);
          }
        } catch (error) {

          if (error?.name !== 'AbortError') renderError = error;
          throw error;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      })
      .finally(() => {
        if (renderError) failRenderWork(renderError);
        else settleRenderWork();
      });
  };

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #updateQuantitySelectors(updatedCart) {
    let totals;
    try {
      totals = quantitiesByVariant(updatedCart.items);
    } catch (error) {

      console.warn('[cart-items] Skipped quantity selector sync:', error);
      return;
    }

    for (const selector of document.querySelectorAll('quantity-selector-component[data-variant-id]')) {
      if (!(selector instanceof HTMLElement)) continue;
      const input = selector.querySelector('input[data-cart-quantity]');
      if (!(input instanceof HTMLInputElement)) continue;

      input.dataset.cartQuantity = String(totals.get(selector.dataset.variantId ?? '') ?? 0);
      if (typeof selector.updateCartQuantity === 'function') {
        selector.updateCartQuantity();
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
        (selector).updateButtonStates?.();
    }
  }

  async fetchCartData({ fresh = false } = {}) {
    if (!fresh && this.#pendingCartFetch) return this.#pendingCartFetch;

    const request = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        ...(fresh ? { cache: 'no-store' } : {}),
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })();
    if (fresh) return request;

    this.#pendingCartFetch = request.finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }

  static #isNavigationTiming(entry) {
    return entry?.entryType === 'navigation';
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
